import {
  Autocomplete,
  SxProps,
  TextField,
  Theme,
  debounce,
} from "@mui/material";
import { EmptyData, Loading } from "components/common";
import { FindAllOrgunitDto, OrgunitCompactDto } from "dto/orgunit";
import { useFindAllOrgunitCompact } from "hooks/orgunit/useFindAllOrgunitCompact";
import { useEffect, useState } from "react";

type PropsType = {
  name?: string;
  label?: React.ReactNode;
  margin?: "dense" | "normal" | "none";
  fullWidth?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  value?: number;
  onBlur?: (e: React.FocusEvent<any, Element>) => void;
  setFieldValue?: (value?: number) => void;
  size?: "small" | "medium";
  width?: number;
  defaultSelected?: OrgunitCompactDto;
  allowClear?: boolean;
  sx?: SxProps<Theme>;
  filters?: FindAllOrgunitDto;
  disabled?: boolean;
};

function SelectOrgunit(props: PropsType) {
  const [selected, setSelected] = useState<OrgunitCompactDto | null>(
    props.defaultSelected ?? null
  );
  const [filters, setFilters] = useState<FindAllOrgunitDto>({
    keyword: "",
  });
  const { infiniteQuery, rows, count, loading } = useFindAllOrgunitCompact({
    filters: { ...props.filters, ...filters },
  });

  const debounceSearch = debounce((value?: string) => {
    setFilters((prev) => ({
      ...prev,
      keyword: value,
    }));
  }, 1000);

  useEffect(() => {
    if (!props.value && selected) {
      setSelected(null);
    }
  }, [props.value]);

  return (
    <Autocomplete
      disabled={props.disabled}
      renderInput={(params) => (
        <TextField
          {...params}
          name={props.name}
          label={props.label}
          margin={props.margin}
          error={props.error}
          helperText={props.helperText}
        />
      )}
      disableClearable={!props.allowClear}
      fullWidth={props.fullWidth}
      options={rows}
      filterOptions={() => rows}
      loading={loading}
      loadingText={<Loading height={100} />}
      getOptionLabel={(option?: OrgunitCompactDto) => option?.name ?? ""}
      value={selected}
      onBlur={props.onBlur}
      onChange={(e, value) => {
        setSelected(value);
        if (props.setFieldValue) {
          props.setFieldValue(value?.id ?? undefined);
        }
      }}
      onInputChange={(e, value, reason) => {
        if (reason !== "reset") {
          debounceSearch(value);
        }
      }}
      noOptionsText={<EmptyData />}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      sx={props.sx}
      size={props.size}
      ListboxProps={{
        onScroll: (e: React.SyntheticEvent) => {
          const listboxNode = e.currentTarget;

          if (
            Math.round(listboxNode.scrollTop + listboxNode.clientHeight) ===
              Math.round(listboxNode.scrollHeight) &&
            !loading &&
            rows.length < count
          ) {
            infiniteQuery.fetchNextPage();
          }
        },
      }}
    />
  );
}

export default SelectOrgunit;
